import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Bell, Lock, User } from 'lucide-react';
import useAuthStore from '../store/authStore';
import { countries } from '../data/countries';

const profileSchema = z.object({
  email: z.string().email('Invalid email address'),
  organization: z.string().min(2, 'Organization name must be at least 2 characters'),
  country: z.string().min(2, 'Country is required'),
  companyRegistrationNumber: z.string().optional(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(8, 'Password must be at least 8 characters'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string()
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const notificationSchema = z.object({
  productUpdates: z.boolean(),
  newFeatures: z.boolean(),
  securityAlerts: z.boolean(),
  marketingEmails: z.boolean(),
});

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;
type NotificationForm = z.infer<typeof notificationSchema>;

const Settings: React.FC = () => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);

  const { 
    register: registerProfile, 
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors }
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      email: user?.email || '',
      organization: user?.organization || '',
      country: user?.country || 'South Africa',
      companyRegistrationNumber: user?.companyRegistrationNumber || '',
    }
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors }
  } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema)
  });

  const {
    register: registerNotifications,
    handleSubmit: handleNotificationsSubmit,
    formState: { errors: notificationErrors }
  } = useForm<NotificationForm>({
    defaultValues: {
      productUpdates: true,
      newFeatures: true,
      securityAlerts: true,
      marketingEmails: false,
    }
  });

  const onProfileSubmit = async (data: ProfileForm) => {
    try {
      // TODO: Implement API call to update profile
      console.log('Profile update:', data);
      setUpdateSuccess('Profile updated successfully');
      setTimeout(() => setUpdateSuccess(null), 3000);
    } catch (error) {
      console.error('Profile update error:', error);
    }
  };

  const onPasswordSubmit = async (data: PasswordForm) => {
    try {
      // TODO: Implement API call to update password
      console.log('Password update:', data);
      setUpdateSuccess('Password updated successfully');
      setTimeout(() => setUpdateSuccess(null), 3000);
    } catch (error) {
      console.error('Password update error:', error);
    }
  };

  const onNotificationsSubmit = async (data: NotificationForm) => {
    try {
      // TODO: Implement API call to update notification preferences
      console.log('Notification preferences update:', data);
      setUpdateSuccess('Notification preferences updated successfully');
      setTimeout(() => setUpdateSuccess(null), 3000);
    } catch (error) {
      console.error('Notification preferences update error:', error);
    }
  };

  return (
    <div className="py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Settings</h1>
      
      <div className="max-w-2xl mx-auto">
        {updateSuccess && (
          <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg">
            {updateSuccess}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'profile'
                    ? 'border-cyan-500 text-cyan-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="h-5 w-5 inline-block mr-2" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'password'
                    ? 'border-cyan-500 text-cyan-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Lock className="h-5 w-5 inline-block mr-2" />
                Password
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'notifications'
                    ? 'border-cyan-500 text-cyan-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Bell className="h-5 w-5 inline-block mr-2" />
                Notifications
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    {...registerProfile('email')}
                    className="mt-1 block w-full rounded-md"
                  />
                  {profileErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{profileErrors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
                    Company Name
                  </label>
                  <input
                    type="text"
                    {...registerProfile('organization')}
                    className="mt-1 block w-full rounded-md"
                  />
                  {profileErrors.organization && (
                    <p className="mt-1 text-sm text-red-600">{profileErrors.organization.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                    Country
                  </label>
                  <select
                    {...registerProfile('country')}
                    className="mt-1 block w-full rounded-md border-gray-300 focus:border-cyan-500 focus:ring-cyan-500"
                  >
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  {profileErrors.country && (
                    <p className="mt-1 text-sm text-red-600">{profileErrors.country.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="companyRegistrationNumber" className="block text-sm font-medium text-gray-700">
                    Company Registration Number
                  </label>
                  <input
                    type="text"
                    {...registerProfile('companyRegistrationNumber')}
                    className="mt-1 block w-full rounded-md"
                    placeholder="Optional"
                  />
                  {profileErrors.companyRegistrationNumber && (
                    <p className="mt-1 text-sm text-red-600">{profileErrors.companyRegistrationNumber.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                >
                  Update Profile
                </button>
              </form>
            )}

            {activeTab === 'password' && (
              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                    Current Password
                  </label>
                  <input
                    type="password"
                    {...registerPassword('currentPassword')}
                    className="mt-1 block w-full rounded-md"
                  />
                  {passwordErrors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    {...registerPassword('newPassword')}
                    className="mt-1 block w-full rounded-md"
                  />
                  {passwordErrors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    {...registerPassword('confirmPassword')}
                    className="mt-1 block w-full rounded-md"
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                >
                  Update Password
                </button>
              </form>
            )}

            {activeTab === 'notifications' && (
              <form onSubmit={handleNotificationsSubmit(onNotificationsSubmit)} className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...registerNotifications('productUpdates')}
                      className="h-4 w-4 text-cyan-600 rounded"
                    />
                    <label className="ml-3 block text-sm font-medium text-gray-700">
                      Product Updates
                      <p className="text-sm text-gray-500">Receive notifications about product updates and improvements</p>
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...registerNotifications('newFeatures')}
                      className="h-4 w-4 text-cyan-600 rounded"
                    />
                    <label className="ml-3 block text-sm font-medium text-gray-700">
                      New Features
                      <p className="text-sm text-gray-500">Be the first to know about new features and tools</p>
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...registerNotifications('securityAlerts')}
                      className="h-4 w-4 text-cyan-600 rounded"
                    />
                    <label className="ml-3 block text-sm font-medium text-gray-700">
                      Security Alerts
                      <p className="text-sm text-gray-500">Receive important security-related notifications</p>
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      {...registerNotifications('marketingEmails')}
                      className="h-4 w-4 text-cyan-600 rounded"
                    />
                    <label className="ml-3 block text-sm font-medium text-gray-700">
                      Marketing Emails
                      <p className="text-sm text-gray-500">Receive marketing and promotional emails</p>
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                >
                  Update Notification Preferences
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;